import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import EducatorDetailedCard from '@comp/EducatorDetailedCard/EducatorDetailedCard';
import Dropdown from '@comp/Dropdown/Dropdown';
import {
  fetchFiltersForEducatorList,
  fetchSeeAllTypeFeed
} from '@actions/specialClasses';
import { useDispatchToStore } from '@hooks/reduxHooks';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { breakpoints, Spacings } from '@styles/index';
import { seeAllTypes } from '@constants/index';
import { courseTypes } from '@actions-constants/index';
import { segment } from '@actions/analytics';
import { FILTER_APPLIED } from '@constants/segment';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';

const FiltersContainer = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(18, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    margin-bottom: ${Spacings.SPACING_6B};
    & button {
      width: auto;
      grid-column: span 10;
      height: 45px;
    }
  }
`;

const ProgressContainer = styled.div`
  margin: ${Spacings.SPACING_8B};
  grid-column: span 18;
  justify-content: center;
  display: flex;
`;

const getLanguageList = (filters) =>
  filters.map((filter) => ({
    id: filter.language_id,
    value: filter.language,
    name: filter.language
  }));
const getTopicGroupList = (filters) =>
  filters.map((filter) => ({
    id: filter.uid,
    value: filter.name,
    name: filter.name
  }));

const EducatorFeed = ({
  goalUID,
  educatorFeedBlock,
  defaultBlockURL,
  seeAllType,
  blockType,
  blockId,
  goalkey,
  label,
  useLeaderboardMetaInfo
}) => {
  const [languageOptions, setLanguageOptions] = useState([]);
  const { selectedGoal: goal } = useSelectedGoal();
  const [topicGroupOptions, setTopicGroupOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedTopicGroup, setSelectedTopicGroup] = useState(null);
  const dispatch = useDispatchToStore();

  useEffect(() => {
    dispatch(fetchFiltersForEducatorList(seeAllTypes.GOAL, goalUID)).then(
      (resp) => {
        if (
          resp.type === `${courseTypes.GET_FILTERS_FOR_EDUCATORS_LIST}_SUCCESS`
        ) {
          setLanguageOptions([
            {
              id: '',
              value: 'All languages',
              name: 'All languages'
            },
            ...getLanguageList(resp.result.languages)
          ]);
          setTopicGroupOptions([
            {
              id: '',
              value: 'All subjects',
              name: 'All subjects'
            },
            ...getTopicGroupList(resp.result.topic_groups)
          ]);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalUID]);

  const renderEachEducator = (educator, index) => {
    return (
      <EducatorDetailedCard
        educator={educator}
        key={educator.uid}
        gridColumn="span 9"
        linkStyle={`margin-top: ${
          Spacings.SPACING_6B
        };@media (max-width:47.95em){margin-top:${index === 0 ? '0' : ''}}`}
        label={label}
        dataAnalytics="goal"
        useLeaderboardMetaInfo={useLeaderboardMetaInfo}
        showBadge
      />
    );
  };

  const [isFetching, setIsFetching] = useState(false);

  const handleFilterSelection = (filterType) => (value) => {
    segment.track(FILTER_APPLIED, {
      filter_type: filterType === 'topicGroup' ? 'Subject' : filterType,
      filter_value: value?.name,
      current_screen: 'See All - Educators',
      goal_name: goal?.name || 'NA',
      goal_uid: goal?.uid || 'NA',
      subscription_duration: 'NA',
      subscription_end_date: 'NA',
      last_primary_source: 'See All',
      last_primary_source_section: goal?.name
        ? `${goal.name} - Educators`
        : 'NA'
    });
    if (filterType === 'languages') setSelectedLanguage(value);
    else setSelectedTopicGroup(value);
  };

  const loadMoreEducators = (resetWithoutEmpty = false) => {
    const params = `${
      selectedTopicGroup?.id ? `topic_group_uid=${selectedTopicGroup.id}` : ''
    }${
      selectedLanguage?.id
        ? `${selectedTopicGroup?.id ? '&' : ''}language_id=${
            selectedLanguage.id
          }`
        : ''
    }`;
    setIsFetching(resetWithoutEmpty);
    return dispatch(
      fetchSeeAllTypeFeed(
        goalUID,
        blockType,
        defaultBlockURL,
        blockId,
        seeAllType,
        true,
        resetWithoutEmpty,
        params
      )
    )?.then((res) => {
      setIsFetching(false);
      return res;
    });
  };

  useFirstRenderSkip(() => {
    loadMoreEducators(true);
  }, [selectedLanguage, selectedTopicGroup]);

  if (educatorFeedBlock?.[goalkey]?.results) {
    return (
      <>
        <FiltersContainer>
          {languageOptions.length > 1 && (
            <Dropdown
              options={languageOptions}
              onOptionClick={handleFilterSelection('languages')}
              selectedOption={selectedLanguage || languageOptions[0]}
              placeholder="Select Language"
              menuWidth="176px"
              buttonWidth="176px"
              mwGridColumn="span 5"
              showSelectedBorder={false}
              stripLength={15}
            />
          )}
          {topicGroupOptions.length > 1 && (
            <Dropdown
              options={topicGroupOptions}
              onOptionClick={handleFilterSelection('topicGroup')}
              selectedOption={selectedTopicGroup || topicGroupOptions[0]}
              placeholder="Select Topic"
              menuWidth="176px"
              buttonWidth="176px"
              mwGridColumn="span 5"
              showSelectedBorder={false}
              stripLength={15}
            />
          )}
        </FiltersContainer>
        {isFetching ? (
          <ProgressContainer>
            <CircularLoader />
          </ProgressContainer>
        ) : (
          <InfiniteList
            data={educatorFeedBlock[goalkey]}
            loadMore={loadMoreEducators}
            _renderItem={renderEachEducator}
            gridColumn="span 18"
          />
        )}
      </>
    );
  }
  return null;
};

EducatorFeed.propTypes = {
  goalUID: PropTypes.string.isRequired,
  educatorFeedBlock: PropTypes.objectOf(PropTypes.any).isRequired,
  defaultBlockURL: PropTypes.string.isRequired,
  seeAllType: PropTypes.string.isRequired,
  blockType: PropTypes.string.isRequired,
  blockId: PropTypes.string.isRequired,
  goalkey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  useLeaderboardMetaInfo: PropTypes.bool
};

EducatorFeed.defaultProps = {
  useLeaderboardMetaInfo: false
};

export default EducatorFeed;
